import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
import _inheritsLoose from "@babel/runtime/helpers/inheritsLoose";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _taggedTemplateLiteralLoose from "@babel/runtime/helpers/taggedTemplateLiteralLoose";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _templateObject() {
  var data = _taggedTemplateLiteralLoose(["\n  .ReactModal__Body--open {\n    overflow: hidden;\n  }\n"]);

  _templateObject = function _templateObject() {
    return data;
  };

  return data;
}

import React from 'react';
import PropTypes from 'prop-types';
import styled, { createGlobalStyle } from 'styled-components';
import ReactModal from 'react-modal';
import { isValidElementType } from 'react-is';
import AppElement from '../AppElement';
import CloseButton from '../CloseButton'; // Remove scroll on the body when react-modal is open.

var ReactModalStyle = createGlobalStyle(_templateObject());
/**
 * Modal component that renders [react-modal](https://reactcommunity.org/react-modal/)
 * under the hood, but supplies default styles and a close button. Any remaining
 * props beyond those documented below are passed along to the `ReactModal`
 * component, so [refer to the documentation](https://reactcommunity.org/react-modal/#general-usage).
 *
 * The modal does not have a default size, it will be only as big as its
 * content. Instead of trying to override the modal's styles to set a size, you
 * can just ensure the modal's contents are the size you want. Wrap the children
 * in a container with `width` and `height` set if necessary.
 *
 * To automatically manage the `appElement` prop without needing to use
 * `Modal.setAppElement`, wrap your app in an `<AppElement>` component at its
 * root.
 *
 * ### Theme Properties
 *
 * - `modal.portalStyle`: Styled Components mixin styles to add to the portal.
 * - `modal.overlayStyle`: Styled Components mixin styles to add to the overlay.
 * - `modal.modalStyle`: Styled Components mixin styles to add to the modal.
 *
 */

export var Modal =
/*#__PURE__*/
function (_React$Component) {
  _inheritsLoose(Modal, _React$Component);

  function Modal() {
    return _React$Component.apply(this, arguments) || this;
  }

  var _proto = Modal.prototype;

  _proto.renderCloseButton = function renderCloseButton(closeButton) {
    var onRequestClose = this.props.onRequestClose;

    if (isValidElementType(closeButton)) {
      closeButton = React.createElement(closeButton);
    }

    if (closeButton && onRequestClose) {
      closeButton = React.cloneElement(closeButton, {
        onClick: onRequestClose
      });
    }

    return closeButton;
  };

  _proto.render = function render() {
    var _this = this;

    var _this$props = this.props,
        className = _this$props.className,
        modalClassName = _this$props.modalClassName,
        blur = _this$props.blur,
        _this$props$bodyOpenC = _this$props.bodyOpenClassName,
        bodyOpenClassName = _this$props$bodyOpenC === void 0 ? blur ? 'ReactModal__Body--open ReactModal__Body--blur' : undefined : _this$props$bodyOpenC,
        closeButton = _this$props.closeButton,
        children = _this$props.children,
        props = _objectWithoutPropertiesLoose(_this$props, ["className", "modalClassName", "blur", "bodyOpenClassName", "closeButton", "children"]);

    return React.createElement(AppElement.Consumer, {
      allowDetached: true
    }, function (_ref) {
      var appElement = _ref.appElement,
          isMounting = _ref.isMounting;
      return isMounting ? null : React.createElement(ReactModal, _extends({
        className: modalClassName,
        portalClassName: className,
        bodyOpenClassName: bodyOpenClassName,
        appElement: appElement // Any supplied `appElement` prop should overwrite the one above.

      }, props), children, _this.renderCloseButton(closeButton), React.createElement(ReactModalStyle, null));
    });
  };

  return Modal;
}(React.Component);

_defineProperty(Modal, "setAppElement", ReactModal.setAppElement.bind(ReactModal));

_defineProperty(Modal, "propTypes", _objectSpread({}, ReactModal.propTypes, {
  /**
   * Class name(s) to apply to the outer portal element. Use the `.Overlay`
   * and `.Modal` selectors to target the overlay and modal subcomponents.
   */
  className: PropTypes.string,

  /**
   * A React element or component type (function or string) to render for the
   * close button. An `onClick` prop will automatically be added that calls
   * `onRequestClose`, if provided. The close button will automatically be
   * rendered after any other children.
   *
   * If you don't want the `onClick` handler, or want to map it to a different
   * prop, just pass a function that maps incoming props to the element you
   * want to render. If you don't want a close button (or want to include it
   * in the modal's children yourself), pass `null`.
   *
   * Defaults to the standard close button.
   */
  closeButton: PropTypes.oneOfType([PropTypes.elementType, PropTypes.node]),

  /**
   * Whether to blur the content behind the overlay.
   */
  blur: PropTypes.bool
}));

_defineProperty(Modal, "defaultProps", {
  closeButton: CloseButton,
  blur: false
});

var StyledModal = styled(Modal).attrs( // In `react-modal`:
// - `portalClassName` applies to the portal.
// - `overlayClassName` applies to the overlay.
// - `className` applies to the modal.
// Since the parent relationship is Portal > Overlay > Modal, in order to use
// this easily with `styled-components`, we need to rewire the incoming
// `className` to go to `portalClassName`, and `modalClassName` to go to
// `className`.
function (_ref2) {
  var _ref2$overlayClassNam = _ref2.overlayClassName,
      overlayClassName = _ref2$overlayClassNam === void 0 ? 'Overlay' : _ref2$overlayClassNam,
      _ref2$modalClassName = _ref2.modalClassName,
      modalClassName = _ref2$modalClassName === void 0 ? 'Modal' : _ref2$modalClassName;
  return {
    overlayClassName: overlayClassName,
    modalClassName: modalClassName
  };
}).withConfig({
  displayName: "Modal__StyledModal",
  componentId: "iccy8k-0"
})(["\n  .Overlay {\n    display: flex;\n    position: fixed;\n    top: 0;\n    left: 0;\n    right: 0;\n    bottom: 0;\n    background: rgba(0, 0, 0, 0.6);\n    z-index: 2000;\n    align-items: center;\n    justify-content: center;\n    ", ";\n  }\n\n  .Modal {\n    position: relative;\n    min-width: 56px;\n    min-height: 56px;\n    max-height: 100vh;\n    background: #fff;\n    overflow: auto;\n    outline: none;\n\n    ", ";\n  }\n\n  ", ";\n"], function (props) {
  return props.theme.modal ? props.theme.modal.overlayStyle : null;
}, function (props) {
  return props.theme.modal ? props.theme.modal.modalStyle : null;
}, function (props) {
  return props.theme.modal ? props.theme.modal.portalStyle : null;
});
StyledModal.setAppElement = Modal.setAppElement;
export default StyledModal;